import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { SendCommandsServiceService } from 'src/app/tech-support/services/send-commands-service.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PanelSocketService } from 'src/app/shared/services/panelsocket.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { WebppSocketService } from 'src/app/shared/services/webppSocket.service';
import { TranslateService } from '@ngx-translate/core';
import { Receiver, ReceiverServer } from '../models/receiver-model';
import { PanelsService } from './panels.service';
import { WebappSubscriptionService } from './webapp-subscription.service';
import constants from '../constants';
import { Subscription } from 'rxjs';
import { CompanyUserService } from 'src/app/settings/services/company-user.service';

@Injectable({
  providedIn: 'root',
})
export class ReceiversService {
  isLoadingResults: boolean = false;
  snackBarErrorDuration: number = 10000;
  snackBarDuration = 5000;

  monitoringReportID: number = 173;
  receivers: Receiver[] = [];
  receiverServers: ReceiverServer[] = [];
  dataSource: MatTableDataSource<Receiver>;
  filteredReceivers: Receiver[] = [];
  monitoringStateUp: boolean = true;
  selectedReceiver: string[] = [];

  onReceiversCallback = [];

  private monitoringSubscription: Subscription;

  // Breadcrumbed - Filter Selected Name
  filterName: string;

  constructor(
    public sendCommandService: SendCommandsServiceService,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private socketMP: PanelSocketService,
    public authService: AuthService,
    private panelChangesSocket: WebppSocketService,
    private panelsService: PanelsService,
    private webappSocket: WebappSubscriptionService,
    private companyUserService: CompanyUserService
  ) {
    this.dataSource = new MatTableDataSource(this.receivers);
    this.webappSocket.addSubscription(this.handleIncomingMessages, constants.webappSocketTypes.monitoringUpdate);
    this.panelChangesSocket.connect();
    this.panelChangesSocket.connEvent.subscribe(() => {
      if (this.receivers) this.panelChangesSocket.connectToReceivers(this.receivers);
      // Una reconexión a mitad de la carga significa que se perdieron paquetes de la lista de
      // paneles: hay que pedirla de nuevo porque Socket.IO no los reenvía.
      this.panelsService.reloadPanelListOnReconnect();
    });
    this.panelChangesSocket.requestDataToService(() => {
      const apiKeys = this.receivers.map((r) => r.apiKey);
      this.panelsService.filterAndSendSignalReport(apiKeys);
    });
  }

  handleIncomingMessages = async (message) => {
    const receiver = this.receivers.find((r) => r.apiKey === message.apiKey);
    if (receiver) {
      if (message.monitoringStatus != undefined) receiver.monitoringStatus = message.monitoringStatus;
      receiver.status = message.status;
    }
  };

  causedErrorCloseTranslations = async () => {
    const causedError = await this.translateService.get('Shared.CausedError').toPromise();
    const close = await this.translateService.get('Shared.Close').toPromise();
    return [causedError, close];
  };

  leaveReceiversApiKeysChannels = () => {
    return new Promise((resolve) => {
      this.panelChangesSocket.unsubscribe(this.receivers);
      resolve(true);
    });
  };

  joinReceiversApiKeysChannels = () => {
    return new Promise((resolve) => {
      const receivers = this.companyUserService.adminHasChosenCompany()
        ? this.receivers.filter((receiver) => receiver.company === this.companyUserService.getCompanyIdChosenByAdmin())
        : this.receivers;
      this.panelChangesSocket.connectToReceivers(receivers);
      resolve(true);
    });
  };

  getSelectedReceiverCompany = () => {
    if (this.selectedReceiver.length === 0) return null;
    if (this.selectedReceiver.length > 1) return null;
    if (this.selectedReceiver[0] === 'default') return this.receivers[0]?.company;
    return this.receivers.find((receiver) => receiver.apiKey === this.selectedReceiver[0])?.company;
  }

  getReceivers = async () => {
    return new Promise((resolve) => {
      this.sendCommandService.getAvailableReceivers().subscribe({
        next: (res: any) => {
          if (res?.success) {
            this.receivers = res.receivers.map((receiver) => ({
              ...receiver,
              testPHourConfiguration: {
                lowControl: receiver.testPHourConfiguration?.lowControl ?? 6,
                goodControl: receiver.testPHourConfiguration?.goodControl ?? 10,
              },
            }));
            if (this.receivers) {
              // Init socket listeners for messages-processor & mobapp-service
              this.socketMP.initMPListener(this.receivers.map((r) => r.apiKey));
              this.panelChangesSocket.connectToReceivers(this.receivers);

              this.onReceiversCallback.forEach((cb) => cb());
            }
            this.processReceiverStatus();
          }
          resolve(true);
        },
        error: async (error) => {
          if (error.reason) {
            return this.snackBar.open(error.reason, this.translateService.instant('Shared.Close'), {
              duration: this.snackBarDuration,
            });
          }
          const [causedError, close] = await this.causedErrorCloseTranslations();
          this.snackBar.open(causedError, close, { duration: this.snackBarDuration });
        },
      });
    });
  };

  connectReceivers = () => {
    this.panelChangesSocket.connect();
    if (this.receivers.length === 0) this.getReceivers().then(() => {});

    // connectReceivers() se llama desde el guard, el login y el constructor de AppComponent. Sin
    // esta guarda cada llamada agrega otro suscriptor y el mismo mensaje se procesa N veces.
    if (this.monitoringSubscription) return;
    this.monitoringSubscription = this.socketMP.listenEvent.subscribe((message) => {
      if (
        message.decodification &&
        message.decodification.status &&
        message.decodification.id === this.monitoringReportID
      ) {
        const rec = this.receivers.find((r) => r.apiKey === message.receiver.apiKey);
        rec.monitoringStatus = message.decodification.monitoringStatus;
        rec.status = true;
        if (!message.decodification.monitoringStatus) this.monitoringStateUp = false;
        else this.processReceiverStatus();
      }
    });
  };

  processReceiverStatus = () => {
    // Metodo para verificar si hay almenos una receptora asignada que este sin monitoreo
    this.filteredReceivers = this.receivers.filter((res) => !!res.version);
    if (this.companyUserService.adminHasChosenCompany())
      this.filteredReceivers = this.filteredReceivers.filter(
        (receiver) => receiver.company === this.companyUserService.getCompanyIdChosenByAdmin()
      );
    this.monitoringStateUp = !this.filteredReceivers.find((r) => !r.status || !r.monitoringStatus);
  };

  clear = () => {
    this.panelChangesSocket.unsubscribe(this.receivers);
    this.monitoringSubscription?.unsubscribe();
    this.monitoringSubscription = undefined;
    this.receivers = [];
    this.dataSource.data = [];
    this.filteredReceivers = [];
  };

  requestDataToService = (callback) => {
    this.panelChangesSocket.requestDataToService(callback);
  };

  setReceiverServers = (servers) => {
    this.receiverServers = servers;
  }
}
